import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { View, Text, ScrollView, Pressable, ActivityIndicator } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'expo-router';
import { DateTime } from 'luxon';
import Toast from 'react-native-toast-message';

import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { checkInPerson, enrollRegistrantInPath } from '@/data/events';
import { startFollowUpPath, listFollowUpEnrollments } from '@/data/followUp';
import { buildFollowUpPathOptions } from '@/utils/automationBuilder';
import { showAlert } from '@/utils/showAlert';
import SheetModal from '@/components/General/SheetModal';
import SolidButton from '@/components/General/SolidButton';
import Avatar from '@/components/People/Avatar';
import Unicon from '@/components/General/Unicon';

function getAgeFromDateOfBirth(dateOfBirth) {
    if (!dateOfBirth) return null;
    const birth = DateTime.fromISO(dateOfBirth);
    if (!birth.isValid) return null;
    const now = DateTime.now();
    const age = Math.floor(now.diff(birth, 'years').years);
    return age >= 0 ? age : null;
}

/**
 * Registrant detail modal — shown after a badge scan or when tapping the info
 * button on a group-check-in row. Shows avatar, contact info, registration
 * meta, badge codes, a check-in button, and registrant path enroll buttons.
 *
 * @param {boolean} isOpen
 * @param {Function} onClose
 * @param {string} eventInstanceID - registration-OWNING instance (detail/codes/paths data)
 * @param {string} targetEventInstanceID - instance check-ins land on (defaults to eventInstanceID)
 * @param {string} targetLabel - label for the "Checking in to …" banner (extension flows)
 * @param {object} registrant - { personID, firstName, lastName, dateOfBirth }
 * @param {Array} locations - event instance locations
 * @param {Array} registrantPaths - from Query.eventInstances.registrantPaths
 * @param {Function} onCheckedIn - callback(personID) after a successful check-in
 * @param {boolean} isCheckedIn - live check-in state AT THE CHECK-IN TARGET
 *                                 (targetEventInstanceID || eventInstanceID) from the
 *                                 parent — must NOT be set-wide across an extension set.
 *                                 Initial state for badge scans comes from registrantDetail.
 */
const RegistrantDetailModal = ({ isOpen, onClose, eventInstanceID, targetEventInstanceID, targetLabel, registrant, locations = [], registrantPaths = [], onCheckedIn, isCheckedIn = false }) => {
    const router = useRouter();
    const queryClient = useQueryClient();

    // Registration data (detail, codes, path enrollments, enroll mutations,
    // add-code route) always targets the registration-OWNING instance —
    // eventInstanceID. Only the check-in itself lands on the target instance
    // (which may be an extension of the registration set).
    const checkInTargetInstanceID = targetEventInstanceID || eventInstanceID;
    const personID = registrant?.personID;
    const { data: loggedInUser } = Query.people.loggedInUser();

    // Lightweight person fetch (contact info + avatar) — avoids the heavy getPersonProfile query.
    const { data: person, isFetching: personLoading } = Query.people.basicById({
        id: personID,
        options: { enabled: !!isOpen && !!personID },
    });

    // Registration meta + check-in status (source of truth for badge scans, where the
    // scan payload carries no alreadyCheckedIn flag and the parent's checkedIn set is empty).
    // Attendance is checked on the check-in TARGET instance, not the registration owner.
    const { data: detail } = Query.eventInstances.registrantDetail({
        eventInstanceID,
        personID,
        attendanceEventInstanceID: checkInTargetInstanceID,
        options: { enabled: !!isOpen && !!eventInstanceID && !!personID },
    });

    // Badge codes (read-only here)
    const { data: codes = [] } = Query.eventInstances.registrantCodes({
        eventInstanceID,
        personID,
        options: { enabled: !!isOpen && !!eventInstanceID && !!personID },
    });

    // Enrollment state for registrant paths
    const { data: enrollments = {} } = Query.eventInstances.registrantPathEnrollments({
        eventInstanceID,
        personID,
        options: { enabled: !!isOpen && !!eventInstanceID && !!personID },
    });

    // Checked-in state at the check-in target: parent prop (live group-flow
    // updates) OR the detail query (initial/badge scans).
    const checkedIn = isCheckedIn || !!detail?.isCheckedIn;
    const checkedInLocationName = detail?.locationName ?? null;

    // Effective location: 0 locations → null, 1 → auto, 2+ → picker (local state)
    const [selectedLocationID, setSelectedLocationID] = useState(null);
    const effectiveLocationID = locations.length === 0
        ? null
        : locations.length === 1
            ? locations[0].id
            : selectedLocationID;

    // Reset local location selection when modal opens
    useEffect(() => {
        if (isOpen) setSelectedLocationID(locations[0]?.id || null);
    }, [isOpen]);

    const checkInMutation = useMutation({
        mutationFn: () => checkInPerson({ personID, eventInstanceID: checkInTargetInstanceID, locationID: effectiveLocationID }),
        onSuccess: (result) => {
            Toast.show({ type: 'success', text2: result?.alreadyCheckedIn ? 'Already checked in' : 'Checked in successfully', position: 'top' });
            queryClient.invalidateQueries({ queryKey: QueryKeys.eventInstances.registrantDetail(eventInstanceID, personID) });
            onCheckedIn?.(personID);
        },
        onError: () => {
            Toast.show({ type: 'error', text2: 'Failed to check in.', position: 'top' });
        },
    });

    const enrollMutation = useMutation({
        mutationFn: (link) => enrollRegistrantInPath({
            pathID: link.pathID,
            personID,
            enrolledBy: loggedInUser?.id,
            eventInstanceID,
            eventRegistrantPathID: link.id,
        }),
        onSuccess: (_data, link) => {
            queryClient.invalidateQueries({ queryKey: QueryKeys.paths.byId(link.pathID) });
            queryClient.invalidateQueries({ queryKey: QueryKeys.eventInstances.registrantPathEnrollments(eventInstanceID, personID) });
        },
        onError: () => {
            Toast.show({ type: 'error', text2: 'Failed to enroll.', position: 'top' });
        },
    });

    // ── Start Follow Up Path (§14.5/R07) ──
    // Distinct product from the registrant-path links above: legacy links
    // start an automation RUN (pathID → Paths); this picker lists FollowUpPaths
    // and calls follow_up_start_path for the registrant's resolved person.
    const canStartFollowUp = !!loggedInUser?.can?.('View Follow Up');
    const { data: followUpPaths } = Query.followUp.paths({
        organizationID: loggedInUser?.organizationID,
        options: { enabled: !!isOpen && canStartFollowUp && !!loggedInUser?.organizationID },
    });
    const followUpPathOptions = useMemo(() => buildFollowUpPathOptions(followUpPaths), [followUpPaths]);
    const [followUpPickerOpen, setFollowUpPickerOpen] = useState(false);

    const followUpEnrollmentsQuery = useQuery({
        queryKey: QueryKeys.followUp.enrollments(loggedInUser?.organizationID, `registrant:${personID}`),
        queryFn: () => listFollowUpEnrollments({ filter: { personID, status: 'ACTIVE' }, limit: 20 }),
        enabled: !!isOpen && canStartFollowUp && !!loggedInUser?.organizationID && !!personID,
        staleTime: 1000 * 60 * 2,
    });
    const activeFollowUpPathIDs = useMemo(() => {
        const rows = followUpEnrollmentsQuery.data?.rows || [];
        return new Set(rows.map(r => r.pathID));
    }, [followUpEnrollmentsQuery.data]);

    const followUpMutation = useMutation({
        mutationFn: (followUpPathID) => startFollowUpPath({
            personID,
            pathID: followUpPathID,
            idempotencyKey: `registrant:${eventInstanceID}:${personID}:${followUpPathID}`,
        }),
        onSuccess: (result) => {
            setFollowUpPickerOpen(false);
            if (result?.alreadyOpen) {
                Toast.show({ type: 'info', text2: 'Already on that path — opening the existing enrollment.', position: 'top' });
            } else {
                Toast.show({ type: 'success', text2: 'Follow Up path started!', position: 'top' });
            }
            followUpEnrollmentsQuery.refetch?.();
            if (result?.enrollmentID) {
                onClose?.();
                router.push(`/follow-up/enrollments/${result.enrollmentID}`);
            }
        },
        // Contract errors (denied/invalid) toast through startFollowUpPath.
    });

    const handleNamePress = useCallback(() => {
        if (!personID) return;
        onClose?.();
        router.push('/people/' + personID);
    }, [personID, onClose, router]);

    const canEnroll = loggedInUser?.can?.("Enroll People in Paths") || loggedInUser?.isAdmin;
    const avatarEditable = loggedInUser?.id == personID || loggedInUser?.can?.("Edit Profile Picture");
    // Match the event-instance registrant list gate for code management (Codes option → canCreateEvents).
    const canManageCodes = loggedInUser?.can?.("Create Events");

    // Filter out inactive paths from the enroll buttons
    const activePathLinks = useMemo(() => registrantPaths.filter(link => link.Path?.isActive !== false), [registrantPaths]);

    const age = getAgeFromDateOfBirth(registrant?.dateOfBirth || person?.dateOfBirth);
    const fullName = `${registrant?.firstName || person?.firstName || ''} ${registrant?.lastName || person?.lastName || ''}`.trim();

    // Push the badge-code scanner WITHOUT closing this modal — SheetModal's
    // stayOpenOnBlur keeps it alive and restores it when the scanner pops back.
    const handleAddCode = useCallback(() => {
        if (!eventInstanceID || !personID) return;
        router.push({
            pathname: `/events/instances/${eventInstanceID}/add-registration-code`,
            params: { eventInstanceID, personID, personName: fullName },
        });
    }, [router, eventInstanceID, personID, fullName]);

    const renderMetaRow = (label, value) => {
        if (!value && value !== 0) return null;
        return (
            <View style={styles.metaRow}>
                <Text style={styles.metaLabel}>{label}</Text>
                <Text style={styles.metaValue}>{value}</Text>
            </View>
        );
    };

    return (
        <SheetModal
            isOpen={isOpen}
            onCancel={onClose}
            onClose={onClose}
            title={fullName || 'Registrant'}
            cancelText="Close"
            stayOpenOnBlur
        >
            <ScrollView style={styles.container} contentContainerStyle={styles.content} keyboardShouldPersistTaps="handled" testID={`registrant-detail-${personID}`}>
                {/* Header: Avatar + name */}
                <View style={styles.header}>
                    <Avatar
                        size={80}
                        profile={person ? { ...person, id: personID } : { id: personID, firstName: registrant?.firstName, lastName: registrant?.lastName }}
                        editable={avatarEditable}
                        disableBorder
                    />
                    <Pressable onPress={handleNamePress} style={styles.namePress}>
                        <Text style={styles.name}>{fullName}</Text>
                        <Unicon name="chevron-forward" size={16} colorVariant="textSecondary" />
                    </Pressable>
                </View>

                {personLoading && !person ? (
                    <View style={styles.loadingRow}>
                        <ActivityIndicator />
                    </View>
                ) : (
                    <>
                        {/* Contact info */}
                        <View style={styles.section}>
                            <Text style={styles.sectionTitle}>Contact</Text>
                            {renderMetaRow('Email', person?.email)}
                            {renderMetaRow('Phone', person?.Phones?.[0]?.phoneNumber)}
                            {renderMetaRow('Age', age !== null ? String(age) : null)}
                            {person?.Address && renderMetaRow('Address', [person.Address.street, person.Address.city, person.Address.state].filter(Boolean).join(', '))}
                            {!person?.email && !person?.Phones?.[0]?.phoneNumber && age === null && !person?.Address ? (
                                <Text style={styles.emptyText}>No contact info</Text>
                            ) : null}
                        </View>

                        {/* Registration meta */}
                        <View style={styles.section}>
                            <Text style={styles.sectionTitle}>Registration</Text>
                            {renderMetaRow('Registered', detail?.registeredAt ? DateTime.fromISO(detail.registeredAt).toFormat('MMM d, yyyy h:mm a') : null)}
                            {renderMetaRow('Source', detail?.source)}
                            {renderMetaRow('Registered By', detail?.registeredByName)}
                            {renderMetaRow('Checked In', checkedIn ? (checkedInLocationName ? `Yes — ${checkedInLocationName}` : 'Yes') : 'No')}
                        </View>

                        {/* Codes — always visible; staff can attach a badge code via the scanner */}
                        <View style={styles.section}>
                            <View style={styles.sectionHeaderRow}>
                                <Text style={styles.sectionTitle}>Codes</Text>
                                {canManageCodes && (
                                    <Pressable onPress={handleAddCode} hitSlop={8} testID="registrant-detail-add-code">
                                        <Text style={styles.addCodeText}>Add Code</Text>
                                    </Pressable>
                                )}
                            </View>
                            {codes.length === 0 ? (
                                <Text style={styles.emptyText}>No Codes Attached</Text>
                            ) : (
                                codes.map(code => (
                                    <View key={code.id} style={styles.codeRow}>
                                        <Text style={styles.codeText} numberOfLines={1}>{code.code}</Text>
                                        <View style={[styles.statusChip, code.isActive ? styles.statusActive : styles.statusRevoked]}>
                                            <Text style={[styles.statusChipText, code.isActive ? styles.statusActiveText : styles.statusRevokedText]}>
                                                {code.isActive ? 'Active' : 'Revoked'}
                                            </Text>
                                        </View>
                                    </View>
                                ))
                            )}
                        </View>

                        {/* Divider before check-in section */}
                        <View style={styles.divider} />

                        {/* Location picker (only 2+ locations) */}
                        {locations.length >= 2 && (
                            <View style={styles.section}>
                                <Text style={styles.sectionTitle}>Check-in Location</Text>
                                <View style={styles.locationPills}>
                                    {locations.map(loc => (
                                        <Pressable
                                            key={loc.id}
                                            style={[styles.locationPill, effectiveLocationID === loc.id && styles.locationPillSelected]}
                                            onPress={() => setSelectedLocationID(loc.id)}
                                        >
                                            <Text style={[styles.locationPillText, effectiveLocationID === loc.id && styles.locationPillTextSelected]}>
                                                {loc.name}
                                            </Text>
                                        </Pressable>
                                    ))}
                                </View>
                            </View>
                        )}

                        {/* Target banner (extended registration) */}
                        {targetLabel && (
                            <View style={styles.targetBanner}>
                                <Unicon name="calendar-outline" size={14} colorVariant="primary" />
                                <Text style={styles.targetBannerText}>Checking in to {targetLabel}</Text>
                            </View>
                        )}

                        {/* Check In button */}
                        <View style={styles.checkInRow}>
                            <SolidButton
                                size="md"
                                type={checkedIn ? "secondary" : "primary"}
                                text={checkedIn ? "Checked In" : "Check In"}
                                disabled={checkedIn}
                                processing={checkInMutation.isPending}
                                onPress={() => checkInMutation.mutate()}
                                testID={`registrant-detail-checkin-${personID}`}
                            />
                        </View>

                        {/* Registrant Paths section (only if can enroll AND there are active path links) */}
                        {canEnroll && activePathLinks.length > 0 && (
                            <View style={styles.section}>
                                <Text style={styles.sectionTitle}>Run Automation</Text>
                                {activePathLinks.map(link => {
                                    const isEnrolled = !!enrollments[link.pathID];
                                    const isPending = enrollMutation.isPending && enrollMutation.variables?.id === link.id;
                                    const handleEnrollPress = () => {
                                        if (isEnrolled) {
                                            showAlert({
                                                title: 'Already Running',
                                                subtitle: `${registrant?.firstName || 'This person'} is already running in ${link.name}. Start another run?`,
                                                confirmText: 'Run Again',
                                                onConfirm: () => enrollMutation.mutate(link),
                                            });
                                        } else {
                                            enrollMutation.mutate(link);
                                        }
                                    };
                                    return (
                                        <View key={link.id} style={styles.pathButtonWrap}>
                                            <SolidButton
                                                size="sm"
                                                variant="muted"
                                                type={isEnrolled ? "secondary" : "primary"}
                                                text={isEnrolled ? `${link.name} — Running` : link.name}
                                                disabled={isPending}
                                                processing={isPending}
                                                onPress={handleEnrollPress}
                                                testID={`registrant-path-${link.id}`}
                                            />
                                        </View>
                                    );
                                })}
                            </View>
                        )}

                        {/* Start Follow Up Path — distinct semantic (§14.5):
                            FollowUpPaths, not automation runs. person = the
                            registrant's resolved person. */}
                        {canStartFollowUp && personID && followUpPathOptions.length > 0 && (
                            <View style={styles.section} testID="registrant-follow-up-section">
                                <Text style={styles.sectionTitle}>Follow Up</Text>
                                <SolidButton
                                    size="sm"
                                    variant="muted"
                                    type="primary"
                                    text="Start Follow Up Path"
                                    iconName="heart-outline"
                                    onPress={() => setFollowUpPickerOpen(true)}
                                    testID="registrant-start-follow-up"
                                />
                            </View>
                        )}
                    </>
                )}
            </ScrollView>

            {/* Follow Up path picker — kind-aware labels (Structured vs Care) */}
            <SheetModal
                isOpen={followUpPickerOpen}
                onCancel={() => setFollowUpPickerOpen(false)}
                onClose={() => setFollowUpPickerOpen(false)}
                title="Start Follow Up Path"
                cancelText="Cancel"
            >
                <ScrollView style={styles.followUpPickerList} keyboardShouldPersistTaps="handled">
                    <Text style={styles.followUpPickerHint}>
                        Starts a Follow Up journey for this person — separate from automation runs.
                    </Text>
                    {followUpPathOptions.map(opt => {
                        const alreadyActive = activeFollowUpPathIDs.has(opt.value);
                        return (
                            <Pressable
                                key={opt.value}
                                style={styles.followUpOption}
                                onPress={() => !followUpMutation.isPending && followUpMutation.mutate(opt.value)}
                                testID={`follow-up-path-${opt.value}`}
                            >
                                <View style={styles.followUpOptionText}>
                                    <Text style={styles.followUpOptionName}>{opt.label}</Text>
                                    {alreadyActive && <Text style={styles.followUpOptionActive}>Already active</Text>}
                                </View>
                                <Unicon name="chevron-forward" size={16} colorVariant="textSecondary" />
                            </Pressable>
                        );
                    })}
                </ScrollView>
            </SheetModal>
        </SheetModal>
    );
};

const styles = StyleSheet.create(({ colors, fonts }) => ({
    container: {
        flex: 1,
    },
    content: {
        padding: 16,
        gap: 16,
        paddingBottom: 40,
    },
    header: {
        alignItems: 'center',
        gap: 12,
    },
    namePress: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
    },
    name: {
        fontSize: 20,
        fontFamily: fonts.heading,
        color: colors.text,
    },
    loadingRow: {
        paddingVertical: 24,
        alignItems: 'center',
    },
    section: {
        gap: 8,
    },
    sectionHeaderRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    addCodeText: {
        fontSize: 13,
        fontFamily: fonts.bold,
        color: colors.primary,
    },
    sectionTitle: {
        fontSize: 13,
        fontFamily: fonts.bold,
        color: colors.textSecondary,
        textTransform: 'uppercase',
    },
    metaRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    metaLabel: {
        fontSize: 14,
        fontFamily: fonts.body,
        color: colors.textSecondary,
    },
    metaValue: {
        fontSize: 14,
        fontFamily: fonts.body,
        color: colors.text,
        flexShrink: 1,
        textAlign: 'right',
    },
    emptyText: {
        fontSize: 14,
        fontFamily: fonts.body,
        color: colors.textSecondary,
    },
    codeRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        backgroundColor: colors.surface,
        borderRadius: 8,
        padding: 12,
    },
    codeText: {
        fontSize: 14,
        fontFamily: fonts.bold,
        color: colors.text,
        flex: 1,
    },
    statusChip: {
        paddingHorizontal: 8,
        paddingVertical: 2,
        borderRadius: 10,
    },
    statusActive: {
        backgroundColor: colors.successContainer,
    },
    statusRevoked: {
        backgroundColor: colors.errorContainer,
    },
    statusChipText: {
        fontSize: 11,
        fontFamily: fonts.bold,
        textTransform: 'uppercase',
    },
    statusActiveText: {
        color: colors.success,
    },
    statusRevokedText: {
        color: colors.error,
    },
    locationPills: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 8,
    },
    locationPill: {
        paddingHorizontal: 12,
        paddingVertical: 6,
        borderRadius: 100,
        borderWidth: 1,
        borderColor: colors.border,
    },
    locationPillSelected: {
        backgroundColor: colors.primary,
        borderColor: colors.primary,
    },
    locationPillText: {
        fontSize: 14,
        fontFamily: fonts.body,
        color: colors.text,
    },
    locationPillTextSelected: {
        color: colors.onPrimary,
    },
    checkInRow: {
        alignItems: 'center',
    },
    targetBanner: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        backgroundColor: colors.primaryContainer,
        paddingHorizontal: 12,
        paddingVertical: 8,
        borderRadius: 8,
    },
    targetBannerText: {
        fontSize: 13,
        fontFamily: fonts.body,
        color: colors.primary,
        flexShrink: 1,
    },
    divider: {
        height: 1,
        backgroundColor: colors.border,
        marginVertical: 4,
    },
    pathButtonWrap: {
        alignSelf: 'stretch',
    },
    followUpPickerList: {
        maxHeight: 380,
        paddingHorizontal: 16,
        paddingBottom: 24,
    },
    followUpPickerHint: {
        fontSize: 13,
        fontFamily: fonts.body,
        color: colors.textSecondary,
        lineHeight: 19,
        marginBottom: 12,
    },
    followUpOption: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        backgroundColor: colors.surface,
        borderRadius: 10,
        padding: 14,
        marginBottom: 8,
    },
    followUpOptionText: { flex: 1 },
    followUpOptionName: {
        fontSize: 14,
        fontFamily: fonts.bold,
        color: colors.text,
    },
    followUpOptionActive: {
        fontSize: 11,
        fontFamily: fonts.body,
        color: colors.success,
        marginTop: 2,
    },
}));

export default RegistrantDetailModal;
